const CHORD_TOKEN_RE = /^[A-G](?:#|b)?(?:maj|min|m|sus|dim|aug|add)?\d*(?:\/[A-G](?:#|b)?)?$/i;
const KEY_TOKEN_RE = /^[A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?$/i;

const normalizeLineBreaks = (value = '') => String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

const splitDelimitedSongBlocks = (text = '') => {
  const byDivider = text
    .split(/\n\s*(?:---+|===+)\s*\n/g)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
  if (byDivider.length > 1) return byDivider;
  return [text.trim()].filter(Boolean);
};

const splitOnStructuralBreaks = (text = '') => {
  const normalized = normalizeLineBreaks(text)
    .replace(/\f/g, '\n\n\n')
    .replace(/\n\s*(?:[_=-]{3,}|\*{3,})\s*\n/g, '\n\n\n');
  const byGaps = normalized
    .split(/\n{4,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
  return byGaps.length > 1 ? byGaps : [normalized.trim()].filter(Boolean);
};

const isChordOnlyLine = (line = '') => {
  const cleaned = line
    .replace(/[()[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return false;
  const tokens = cleaned.split(/[\s,|]+/).filter(Boolean);
  if (!tokens.length) return false;
  return tokens.every((token) => CHORD_TOKEN_RE.test(token));
};

const extractCapo = (text = '') => {
  let line = text;
  let capo = '';
  const capoMatch = line.match(/\b(?:capo\s*|c)(\d{1,2})\b/i);
  if (capoMatch) {
    capo = `Capo ${capoMatch[1]}`;
    line = line.replace(capoMatch[0], ' ');
  }
  return { line: line.replace(/\s{2,}/g, ' ').trim(), capo };
};

const parseNumberedSongBlocks = (text = '') => {
  const lines = normalizeLineBreaks(text).split('\n');
  const numberedCount = lines.filter((line) => /^\s*\d+\.\s+/.test(line)).length;
  if (numberedCount < 2) return null;
  const blocks = [];
  let current = null;
  const pushCurrent = () => {
    if (!current) return;
    blocks.push([current.first, ...current.rest].join('\n').trim());
  };
  lines.forEach((line) => {
    const numbered = line.match(/^\s*\d+\.\s*(.*?)\s*$/);
    if (numbered) {
      const content = numbered[1] || '';
      if (current && isChordOnlyLine(content)) {
        current.rest.push(content);
        return;
      }
      pushCurrent();
      current = { first: content, rest: [] };
      return;
    }
    if (current) current.rest.push(line);
  });
  pushCurrent();
  return blocks.filter(Boolean);
};

const parseTitleAndKey = (line = '') => {
  let working = line.trim();
  if (!working) return { title: '', key: '', quickNote: '' };

  let key = '';
  let quickNote = '';

  const wrappedKey = working.match(/[[()]\s*(?:key(?:\s+of)?\s*:?)?\s*([A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?)\s*[\])]/i);
  if (wrappedKey) {
    key = (wrappedKey[1] || '').trim();
    working = working.replace(wrappedKey[0], ' ');
  }

  if (!key) {
    const keyLabelMatch = working.match(/\b(?:key(?:\s+of)?|in)\s*[:=-]?\s*([A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?)(?=$|[\s,.;:!?])/i);
    if (keyLabelMatch) {
      key = (keyLabelMatch[1] || '').trim();
      working = working.replace(keyLabelMatch[0], ' ');
    }
  }

  const capoFromLine = extractCapo(working);
  if (capoFromLine.capo) quickNote = capoFromLine.capo;
  working = capoFromLine.line;

  if (!key) {
    const trailingWithCapo = line.match(/\b([A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?)\s+(?:capo\s*|c)(\d{1,2})\b/i);
    if (trailingWithCapo) {
      key = (trailingWithCapo[1] || '').trim();
      if (!quickNote) quickNote = `Capo ${trailingWithCapo[2]}`;
      working = working.replace(trailingWithCapo[1], ' ');
    }
  }

  if (!key) {
    const trailingKey = working.match(/^(.*?)(?:\s[-|–—]\s|\s{2,})([A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?)\.?$/i);
    if (trailingKey && KEY_TOKEN_RE.test((trailingKey[2] || '').trim())) {
      key = (trailingKey[2] || '').trim();
      working = (trailingKey[1] || '').trim();
    }
  }

  if (!key) {
    const trailingSimpleKey = working.match(/^(.*?)\s([A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?)\.?$/i);
    if (trailingSimpleKey && KEY_TOKEN_RE.test((trailingSimpleKey[2] || '').trim())) {
      const titleCandidate = (trailingSimpleKey[1] || '').trim();
      if (titleCandidate.split(/\s+/).filter(Boolean).length >= 2) {
        key = (trailingSimpleKey[2] || '').trim();
        working = titleCandidate;
      }
    }
  }

  working = working.replace(/\bkey\b\s*$/i, '').replace(/\s*[,:;.-]+\s*$/g, '').replace(/\s{2,}/g, ' ').trim();
  return { title: working, key, quickNote };
};

const uppercaseWordRatio = (line = '') => {
  const words = line.split(/\s+/).map((w) => w.replace(/[^A-Za-z']/g, '')).filter(Boolean);
  if (!words.length) return 0;
  const upperish = words.filter((w) => /^[A-Z]/.test(w)).length;
  return upperish / words.length;
};

const isLikelyTitleLine = (line = '', nextLine = '') => {
  const trimmed = String(line || '').trim();
  if (!trimmed) return false;
  if (isChordOnlyLine(trimmed)) return false;
  if (trimmed.length > 100) return false;
  if (/^[[("]/.test(trimmed)) return false;
  if (/^[a-z]/.test(trimmed)) return false;

  const hasKeyOrCapo = /\b(?:key(?:\s+of)?|in)\b/i.test(trimmed) ||
    /\b(?:capo\s*|c)\d{1,2}\b/i.test(trimmed) ||
    /(?:\(|\[)\s*[A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?\s*(?:\)|\])/i.test(trimmed);
  if (!hasKeyOrCapo) return false;

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 10) return false;
  if (/[.!?]$/.test(trimmed)) return false;
  if ((trimmed.match(/,/g) || []).length > 1) return false;
  if (uppercaseWordRatio(trimmed) < 0.65) return false;

  const next = String(nextLine || '').trim();
  if (!next) return true;
  if (isChordOnlyLine(next)) return true;
  if (/^\[?[A-G](?:#|b)?m?(?:\]|\b)/.test(next)) return true;
  return false;
};

const parseChordProLikeText = (text = '') => {
  const lines = normalizeLineBreaks(text).split('\n');
  const songs = [];
  let current = null;
  const pushCurrent = () => {
    if (!current) return;
    const finalSong = {
      title: (current.title || '').trim(),
      key: (current.key || '').trim(),
      quickNote: '',
      lyrics: current.lines.join('\n').trim(),
      chords: ''
    };
    if (finalSong.title || finalSong.lyrics) songs.push(finalSong);
  };
  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const titleMatch = line.match(/^\{\s*title\s*:\s*(.*?)\s*\}$/i);
    if (titleMatch) {
      pushCurrent();
      current = { title: titleMatch[1] || '', key: '', lines: [] };
      return;
    }
    if (!current) current = { title: '', key: '', lines: [] };
    const keyMatch = line.match(/^\{\s*key\s*:\s*(.*?)\s*\}$/i);
    if (keyMatch) {
      current.key = keyMatch[1] || '';
      return;
    }
    if (/^\{\s*.*?\s*\}$/.test(line)) return;
    current.lines.push(line);
  });
  pushCurrent();
  return songs;
};

const parseSongBlock = (block = '') => {
  const lines = block.split('\n').map((line) => line.trimEnd());
  const nonEmptyLines = lines.filter((line) => line.trim());
  if (nonEmptyLines.length === 0) return null;
  const firstLine = nonEmptyLines[0];
  const { title, key, quickNote } = parseTitleAndKey(firstLine);
  const firstLineIndex = lines.findIndex((line) => line.trim() === firstLine.trim());
  const bodyLines = lines.slice(firstLineIndex + 1).map((line) => line.trimEnd());
  const chordLines = [];
  const lyricLines = [];
  bodyLines.forEach((line) => {
    if (!line.trim()) {
      lyricLines.push(line);
      return;
    }
    if (isChordOnlyLine(line)) chordLines.push(line.trim());
    else lyricLines.push(line);
  });
  return {
    title,
    key,
    quickNote,
    lyrics: lyricLines.join('\n').trim(),
    chords: chordLines.join('\n').trim()
  };
};

const mergeContinuationSongs = (songs = []) => {
  const merged = [];
  songs.forEach((song) => {
    const title = String(song.title || '').trim();
    const key = String(song.key || '').trim();
    const quickNote = String(song.quickNote || '').trim();
    const chordBlob = [title, String(song.chords || '').trim(), String(song.lyrics || '').trim()]
      .filter(Boolean)
      .join('\n')
      .trim();
    const chordBlobLines = chordBlob.split('\n').map((line) => line.trim()).filter(Boolean);
    const looksLikeChordContinuation = (
      merged.length > 0 &&
      !key &&
      !quickNote &&
      chordBlobLines.length > 0 &&
      chordBlobLines.every((line) => isChordOnlyLine(line))
    );
    if (looksLikeChordContinuation) {
      const prev = merged[merged.length - 1];
      prev.chords = [String(prev.chords || '').trim(), chordBlob].filter(Boolean).join('\n').trim();
      return;
    }
    merged.push({ ...song });
  });
  return merged;
};

const splitByTitleBoundaries = (text = '') => {
  const lines = normalizeLineBreaks(text).split('\n');
  const starts = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const nextNonEmpty = lines.slice(i + 1).find((l) => String(l || '').trim()) || '';
    if (isLikelyTitleLine(line, nextNonEmpty)) starts.push(i);
  }
  if (starts.length < 2) return null;

  const blocks = [];
  for (let idx = 0; idx < starts.length; idx += 1) {
    const start = starts[idx];
    const end = starts[idx + 1] ?? lines.length;
    const block = lines.slice(start, end).join('\n').trim();
    if (block) blocks.push(block);
  }
  return blocks.length > 1 ? blocks : null;
};

const startsWithExplicitSongHeader = (block = '') => {
  const firstLine = normalizeLineBreaks(block).split('\n').find((line) => String(line || '').trim()) || '';
  const trimmed = firstLine.trim();
  if (!trimmed) return false;
  if (/^\d+\.\s+/.test(trimmed)) return true;
  if (/\b(?:key(?:\s+of)?|in)\b/i.test(trimmed)) return true;
  if (/\b(?:capo\s*|c)\d{1,2}\b/i.test(trimmed)) return true;
  if (/(?:\(|\[)\s*[A-G](?:#|b)?(?:\s*(?:maj|major|min|minor|m|dorian|mixolydian))?\s*(?:\)|\])/i.test(trimmed)) return true;
  const parsed = parseTitleAndKey(trimmed);
  return Boolean(parsed?.key);
};

const mergeWeakStructuralBlocks = (blocks = []) => {
  const merged = [];
  blocks.forEach((block) => {
    if (!block) return;
    if (merged.length === 0) {
      merged.push(block);
      return;
    }
    if (startsWithExplicitSongHeader(block)) {
      merged.push(block);
      return;
    }
    merged[merged.length - 1] = `${merged[merged.length - 1]}\n\n${block}`.trim();
  });
  return merged;
};

export const BULK_TUNE_PARSER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'numbered', label: 'Numbered List' },
  { value: 'chordpro', label: 'ChordPro' },
  { value: 'delimited', label: 'Delimited (---)' },
];

export const parseBulkTuneText = (value = '', parser = 'auto') => {
  const text = normalizeLineBreaks(value).trim();
  if (!text) return [];

  if (parser === 'chordpro') return parseChordProLikeText(text);
  if (parser === 'numbered') {
    const numberedBlocks = parseNumberedSongBlocks(text) || [];
    return mergeContinuationSongs(numberedBlocks.map(parseSongBlock).filter(Boolean));
  }
  if (parser === 'delimited') {
    return mergeContinuationSongs(splitDelimitedSongBlocks(text).map(parseSongBlock).filter(Boolean));
  }

  if (/\{\s*title\s*:/i.test(text)) return parseChordProLikeText(text);
  const numberedBlocks = parseNumberedSongBlocks(text);
  if (numberedBlocks && numberedBlocks.length > 0) {
    return mergeContinuationSongs(numberedBlocks.map(parseSongBlock).filter(Boolean));
  }
  const structuralBlocks = mergeWeakStructuralBlocks(splitOnStructuralBreaks(text));
  if (structuralBlocks.length > 1) {
    return mergeContinuationSongs(structuralBlocks.map(parseSongBlock).filter(Boolean));
  }
  const titleBlocks = splitByTitleBoundaries(text);
  if (titleBlocks && titleBlocks.length > 0) {
    return mergeContinuationSongs(titleBlocks.map(parseSongBlock).filter(Boolean));
  }
  return mergeContinuationSongs(splitDelimitedSongBlocks(text).map(parseSongBlock).filter(Boolean));
};

export function parseSpreadsheetTunes(text = '') {
  const lines = normalizeLineBreaks(text).split('\n').filter(l => l.trim());
  if (!lines.length) return [];

  const hasTab = lines[0].includes('\t');
  const splitRow = hasTab
    ? (line) => line.split('\t').map(c => c.trim())
    : (line) => {
        const result = []; let cur = '', inQ = false;
        for (const ch of line) {
          if (ch === '"') { inQ = !inQ; }
          else if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; }
          else cur += ch;
        }
        result.push(cur.trim());
        return result;
      };

  const HEADER_WORDS = new Set(['title','tune','name','type','key','status','genre','category','composer','tags','notes','rating']);
  const firstRow = splitRow(lines[0]);
  const isHeader = firstRow.some(c => HEADER_WORDS.has(c.toLowerCase().replace(/[^a-z]/g, '')));

  const colMap = {};
  if (isHeader) {
    firstRow.forEach((col, i) => {
      const k = col.toLowerCase().replace(/[^a-z]/g, '');
      if (['title','tune','name'].includes(k)) colMap.title = i;
      else if (k === 'type') colMap.type = i;
      else if (k === 'key') colMap.key = i;
      else if (k === 'status') colMap.status = i;
      else if (['genre','category'].includes(k)) colMap.genre = i;
      else if (k === 'composer') colMap.composer = i;
      else if (k === 'tags') colMap.tags = i;
      else if (k === 'notes') colMap.notes = i;
      else if (k === 'rating') colMap.rating = i;
    });
  } else {
    Object.assign(colMap, { title: 0, type: 1, key: 2, status: 3, genre: 4 });
  }

  const dataRows = isHeader ? lines.slice(1) : lines;
  const get = (cols, field) => (colMap[field] !== undefined ? (cols[colMap[field]] || '') : '').trim();

  return dataRows
    .map(line => splitRow(line))
    .filter(cols => get(cols, 'title'))
    .map(cols => ({
      title: get(cols, 'title'),
      type: get(cols, 'type'),
      key: get(cols, 'key'),
      status: get(cols, 'status'),
      genre: get(cols, 'genre'),
      composer: get(cols, 'composer'),
      tags: get(cols, 'tags').split(/[;,]/).map(t => t.trim()).filter(Boolean),
      notes: get(cols, 'notes'),
      rating: get(cols, 'rating'),
    }));
}
